import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function AdminPrescriptionView() {
  const { id } = useParams();
  const { authHeader } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/admin/prescriptions/${id}`, { headers: authHeader() })
      .then(({ data }) => setP(data))
      .catch(() => setP(null))
      .finally(() => setLoading(false));
  }, [id, authHeader]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-sans text-[#566158]">
        <Loader2 className="animate-spin mr-2" size={20} /> Loading...
      </div>
    );
  }

  if (!p) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-serif text-2xl text-[#2C3C30]">Prescription not found</p>
        <Link to="/admin" className="font-sans text-sm underline text-[#2C3C30]">Back to dashboard</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EAE7E0]" data-testid="admin-prescription-view">
      {/* Toolbar (hidden in print) */}
      <div className="no-print bg-[#2C3C30] text-[#F7F6F2]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-[#D1D6CC] text-sm hover:text-[#F7F6F2]">
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="print-prescription-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#F7F6F2] text-[#2C3C30] px-5 py-2.5 text-sm font-medium hover:bg-[#D1D6CC] transition-colors"
          >
            <Printer size={16} /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Letterhead */}
      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-10">
        <div className="prescription-sheet bg-white rounded-2xl shadow-sm p-10 md:p-14 border border-[#2C3C30]/10">
          <div className="flex items-start justify-between gap-6 border-b border-[#2C3C30]/15 pb-6">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="Adya Nutrition" className="w-16 h-16 object-contain" />
              <div>
                <p className="font-serif text-3xl text-[#2C3C30] tracking-tight leading-none">Adya Nutrition</p>
                <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#566158] mt-1">
                  Clinical & Integrative Nutrition
                </p>
              </div>
            </div>
            <div className="text-right font-sans text-sm text-[#566158] leading-relaxed">
              <p>Dt. Darsh Tank</p>
              <p>+91 81082 73005</p>
              <p>helloadya4@gmail.com</p>
              <p>adyanutrition.com</p>
            </div>
          </div>

          {/* Patient block */}
          <div className="flex flex-wrap justify-between gap-4 mt-8 font-sans">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#7D8C7A]">Patient</p>
              <p className="font-serif text-2xl text-[#2C3C30] mt-1">{p.patient_name}</p>
              {(p.patient_phone || p.patient_email) && (
                <p className="text-sm text-[#566158] mt-1">
                  {[p.patient_phone, p.patient_email].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-[#7D8C7A]">Prescription No.</p>
              <p className="font-mono text-sm text-[#2C3C30] mt-1">{p.number}</p>
              <p className="text-xs uppercase tracking-widest text-[#7D8C7A] mt-3">Consultation date</p>
              <p className="font-sans text-sm text-[#2C3C30] mt-1">{fmtDate(p.consultation_date)}</p>
            </div>
          </div>

          {/* Summary */}
          {p.summary && (
            <div className="mt-8">
              <p className="font-sans text-xs uppercase tracking-widest text-[#7D8C7A] mb-2">Summary</p>
              <p className="font-sans text-[#1A211D] leading-relaxed whitespace-pre-wrap">{p.summary}</p>
            </div>
          )}

          {/* Diet chart */}
          {p.diet_chart && p.diet_chart.length > 0 && (
            <div className="mt-8">
              <p className="font-sans text-xs uppercase tracking-widest text-[#7D8C7A] mb-4">Diet chart</p>
              <table className="w-full border-collapse">
                <tbody>
                  {p.diet_chart.map((s, i) => (
                    <tr key={i} className="align-top border-t border-[#2C3C30]/10">
                      <td className="py-4 pr-6 font-serif text-lg text-[#2C3C30] w-1/3">{s.title}</td>
                      <td className="py-4 font-sans text-[#1A211D] leading-relaxed whitespace-pre-wrap">{s.items}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Notes */}
          {p.notes && (
            <div className="mt-10">
              <p className="font-sans text-xs uppercase tracking-widest text-[#7D8C7A] mb-2">Notes & recommendations</p>
              <p className="font-sans text-[#1A211D] leading-relaxed whitespace-pre-wrap">{p.notes}</p>
            </div>
          )}

          {/* Footer / signature */}
          <div className="mt-12 pt-6 border-t border-[#2C3C30]/15 flex flex-col md:flex-row justify-between gap-4">
            <p className="font-sans text-xs text-[#7D8C7A]">
              This nutrition plan is personalised. Please share any changes in symptoms or medications.
            </p>
            <div className="text-right">
              <p className="font-serif text-lg text-[#2C3C30]">Dt. Darsh Tank</p>
              <p className="font-sans text-xs text-[#566158]">Clinical & Integrative Nutritionist</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: #ffffff !important; }
          .no-print { display: none !important; }
          .prescription-sheet { box-shadow: none !important; border: none !important; border-radius: 0 !important; padding: 0 !important; }
          .grain { display: none !important; }
        }
      `}</style>
    </div>
  );
}
